import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from "~/ui-apps/design-system/atoms/Button";
import { Form, useLocation, useNavigate, useRouteLoaderData } from '@remix-run/react';
import axios from 'axios';
import { captureException } from "~/utilities/sentry.utility";
import { resolvePostAuthRedirectPath } from '~/utilities/postAuthLanding';
import type { loader as rootLoader } from '~/root';

const SignUpModal: React.FC<{ handleSwitchToSignIn: () => void, query: string }> = ({ handleSwitchToSignIn, query }) => {
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const turnstileSiteKey = rootData?.turnstileSiteKey ?? '';

  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmittedAndUserCreated, setIsSubmittedAndUserCreated] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidEmail = validateEmail(email);

  useEffect(() => {
    let redirectUrl;
    if (location.pathname === '/screener') {
      redirectUrl = '/screener?query=' + encodeURIComponent(query);
    } else if (location.pathname.includes('ticker')) {
      redirectUrl = location.pathname + location.search;
    } else {
      redirectUrl = resolvePostAuthRedirectPath(location.pathname);
    }
    setRedirectUrl(redirectUrl);
  }, [location.pathname, query]);

  // Load Cloudflare Turnstile script once — reuse if already loaded by another page
  useEffect(() => {
    if (!turnstileSiteKey) return;

    if (window.turnstile) {
      setTurnstileLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      window.onTurnstileLoad = () => setTurnstileLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;
    window.onTurnstileLoad = () => setTurnstileLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, [turnstileSiteKey]);

  // Render the Turnstile widget once a valid email is typed and the script is ready
  useEffect(() => {
    if (!isValidEmail || !turnstileLoaded || !window.turnstile || !turnstileContainerRef.current || !turnstileSiteKey) return;
    if (turnstileWidgetId.current) return;

    turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: turnstileSiteKey,
      callback: (token: string) => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(null),
      'error-callback': () => setTurnstileToken(null),
      theme: 'light',
    });
  }, [isValidEmail, turnstileLoaded, turnstileSiteKey]);

  // Remove widget when email becomes invalid so it re-renders fresh on next valid entry
  useEffect(() => {
    if (!isValidEmail && turnstileWidgetId.current && window.turnstile) {
      window.turnstile.remove(turnstileWidgetId.current);
      turnstileWidgetId.current = null;
      setTurnstileToken(null);
    }
  }, [isValidEmail]);

  const resetTurnstile = useCallback(() => {
    if (turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
      setTurnstileToken(null);
    }
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userEnteredEmail = e.target.value;
    setEmail(userEnteredEmail);
    if (userEnteredEmail.length > 0 && !validateEmail(userEnteredEmail)) {
      setMessage('Please enter a valid email address');
    } else {
      setMessage('');
    }
  };

  const handleSignUp = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (email.length === 0) {
      setMessage('Please enter your email address');
      return;
    }

    if (!turnstileToken) {
      setMessage('Please complete the verification');
      return;
    }

    try {
      const response = await axios.post('/api/sign-up', { email, redirectUrl, turnstileToken });
      if (response.data.status === 'success') {
        setIsSubmittedAndUserCreated(true);
        setMessage("We've emailed you a secured sign-in link. If it's not there, check your spam folders.");
        navigate('/email-landing');
      } else {
        setMessage(response.data.error || response.data.status || 'An error occurred. Please try again.');
        resetTurnstile();
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          setMessage(error.response.data?.error || 'Verification failed. Please try again.');
        } else {
          setMessage('An error occurred while processing your request. Please try again.');
        }
      } else {
        setMessage('An unexpected error occurred. Please try again.');
      }
      resetTurnstile();
      captureException(`Error in handleSignUp: ${error}`);
    }
  };

  return (
    <>
      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="px-6 py-6 sm:px-12">
          {!isSubmittedAndUserCreated ? (
            <>
              <h2 className="mb-8 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                Create Account
              </h2>
              <div>
                <div className="mt-10">
                  <a
                    href={`/google-sign-up?redirectUrl=${redirectUrl}`}
                    className="flex items-center justify-center rounded-full bg-background-dark px-3 py-2 text-sm font-medium text-black shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus-visible:ring-transparent"
                  >
                    <svg className="h-6 w-6" aria-hidden="true" viewBox="0 0 24 24">
                      <path d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z" fill="#EA4335" />
                      <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z" fill="#4285F4" />
                      <path d="M5.26498 14.2949C5.01498 13.5699 4.87498 12.7999 4.87498 11.9999C4.87498 11.1999 5.01498 10.4299 5.26498 9.70492L1.27498 6.60992C0.464983 8.22992 0.000976562 10.0599 0.000976562 11.9999C0.000976562 13.9399 0.464983 15.7699 1.27498 17.3899L5.26498 14.2949Z" fill="#FBBC05" />
                      <path d="M12.0004 24C15.2354 24 17.9504 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.855 13.6204 19.325 12.0004 19.325C8.87537 19.325 6.22037 17.215 5.27537 14.295L1.28537 17.39C3.25537 21.31 7.31037 24 12.0004 24Z" fill="#34A853" />
                    </svg>
                    <span className="text-sm font-semibold mx-2">Google Sign Up</span>
                  </a>
                </div>
                <div className="relative my-6">
                  <div aria-hidden="true" className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm font-medium leading-6">
                    <span className="bg-white px-6 text-gray-900">OR</span>
                  </div>
                </div>
                <Form method="POST" className="space-y-6">
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="Enter your email address"
                      className={`block w-full font-semibold rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ${message ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 placeholder:font-normal focus:ring-2 focus:ring-inset ${message ? 'focus:ring-red-500' : 'focus:ring-green-900'} sm:text-base sm:leading-6`}
                    />
                    {message && (
                      <p className="mt-2 text-sm text-red-600">{message}</p>
                    )}
                  </div>
                  <div>
                    <Button
                      type="primary"
                      size="medium"
                      fullWidth={true}
                      onClick={handleSignUp}
                    >
                      Sign Up
                    </Button>
                  </div>
                  {/* Turnstile widget — shown after user enters a valid email */}
                  {turnstileSiteKey && isValidEmail && (
                    <div className="flex justify-center">
                      <div ref={turnstileContainerRef} />
                    </div>
                  )}
                </Form>
                <p className="mt-10 text-center text-sm text-gray-500">
                  Already have an account?
                  <button onClick={handleSwitchToSignIn} className="font-semibold leading-6 text-green-500 ms-2">
                    Sign In
                  </button>
                </p>
              </div>
            </>
          ) : (
            <div className="text-center">
              <p className="mb-4">{message}</p>
              <p>
                Still missing? <a href="/sign-up" className="text-green-500">try again</a> or contact us at{" "}
                <b className="text-gray-600">support@marketdatafyi.com</b>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export function ErrorBoundary() {
  return (
    <div className="text-center">
      <p className="mb-4">An unexpected error occurred</p>
      <p>contact us at <b className="text-gray-600">support@marketdatafyi.com</b></p>
    </div>
  );
}

export default SignUpModal;
